<template>
	<BaseCmsPage>
		<BaseCmsRotator :fetch="{code: 'main_slider', limit: 8, response_fields: ['id','link','url_without_domain','title','title2','content','image_upload_path','image_thumbs','image_2_thumbs']}" v-slot="{items}" @loadRotatorItems="onLoad">
			<BaseUiSwiper v-if="items?.length" class="main-slider" :options="sliderOptions">
				<BaseUiSwiperSlide v-for="(item,index) in items" :key="item.id">	
					<component :is="item.link ? NuxtLink : 'div'" :to="item.link ? item.url_without_domain : undefined" class="hp-slide">
						<BaseUiImage
								:data="item.image_thumbs?.['width1920-height720']"
								:loading="index > 0 ? 'lazy' : 'eager'"
								default="/images/no-image-1920.jpg"
								:picture="[{maxWidth: '760px', src: item.image_2_thumbs?.['width780-height925'].thumb, default: '/images/no-image-925.jpg'}]" />

						<div class="hp-content-container">
							<div class="hp-inner-content-cnt">
								<h2 v-if="item?.title" class="hp-slider-title">{{item.title}}</h2>
								<div v-if="item?.content" class="hp-slide-content">{{item.content}}</div>
								<div v-if="item?.link && item?.title2" class="btn btn-primary hp-slide-link"><span>{{item.title2}}</span></div>
							</div>
						</div>
					</component>
				</BaseUiSwiperSlide>
			</BaseUiSwiper>
		</BaseCmsRotator>

		<CmsBenefits modeClass="hp-benefits" />

		<BaseCmsRotator :fetch="{code: 'homepage_promo', limit: 4, response_fields: ['id','code', 'link','url_without_domain','title','title2','image_upload_path','image_thumbs']}" v-slot="{items}">
			<div v-if="items?.length" class="hp-promos">
				<div class="wider-wrapper">
					{{sendEvents(items)}}
					<template v-for="item in items" :key="item.id">
						<component :is="item.link ? NuxtLink : 'div'" :to="item.link ? item.url_without_domain : undefined" class="hp-promo" :gtm-tracking="{items: [], promotion_id: item.code, promotion_name: item.title}" @click="item.link ? gtmTrack('selectPromotion', {items: [], promotion_id: item.code, promotion_name: item.title}) : ''">
							<figure class="hp-promo-img">
								<span>
									<BaseUiImage :data="item.image_thumbs?.['width335-height280']" loading="lazy" default="/images/no-image-335.jpg" />							
								</span>
							</figure>
							<div class="hp-promo-cnt">
								<div v-if="item.title2" class="hp-promo-headline">{{item.title2}}</div>
								<div v-if="item.title" class="hp-promo-title">{{item.title}}</div>
							</div>
						</component>
					</template>
				</div>
			</div>
		</BaseCmsRotator>	

		<ClientOnly>
			<BaseCatalogProductsWidget :fetch="{list_code: 'special', sort: 'list_position', limit: 30}" v-slot="{items}" :gtm-tracking="{item_list_id: 'special', item_list_name: labels.get('special_products')}" @loadProductsWidget="onLoadedProducts">
				<div v-if="items?.length" class="special-products hp-special-products">
					<div class="wider-wrapper">
						<div class="special-title hp-special-products-title"><BaseCmsLabel code="special_products" /></div>
						<CatalogSpeciallists :items="items" :perPage="4" />															
					</div>
				</div>				
			</BaseCatalogProductsWidget>
		</ClientOnly>		

		<BaseCatalogManufacturers :fetch="{special: 1, limit: 5, sort: 'position_h', response_fields: ['id','url_without_domain','main_image_thumbs']}" v-slot="{items}">
			<div v-if="items.length" class="hp-brands">
				<div class="wider-wrapper">
					<div class="featured-brands-cnt hp-featured-brands-cnt">
						<NuxtLink v-for="item in items" :key="item.id" :to="item.url_without_domain" class="fb-manufacturer hp-manufacturer">
							<BaseUiImage :data="item.main_image_thumbs?.['width160-height70']" loading="lazy" default="/images/no-image-200.jpg" />							
						</NuxtLink>
						<NuxtLink :to="getAppUrl('manufacturer')" class="hp-manufacturer-all-brands">Svi brandovi</NuxtLink>
					</div>
				</div>
			</div>	
		</BaseCatalogManufacturers>

		<BaseEventEventsWidget :fetch="{limit:8, datetime_published_e: Math.floor(Date.now() / 1000) + (14 * 24 * 60 * 60), sort: 'date', newes: true}" v-slot="{items}">
			<EventHomepage :items="items" />
		</BaseEventEventsWidget>

		<CatalogLandingCategories hydrate-on-visible />

		<BasePublishCategory :fetch="{code: 'advices', response_fields: ['id','code','url_without_domain']}" v-slot="{item}">
			<BasePublishPostsWidget :fetch="{category_code: item.code, limit: 4, extra_fields: ['short_description'], response_fields: ['id', 'url_without_domain', 'title', 'main_image_thumbs', 'short_description']}" v-slot="{items}" v-if="item?.code">
				<div v-if="items?.length" class="hp-blog">
					<div class="wider-wrapper">
						<div class="special-title hp-blog-title"><BaseCmsLabel code="hp_blog" /></div>
						<div class="publish-flex">
							<div class="p-featured">
								<PublishIndexEntry :item="items[0]" mode="big" />
							</div>
							<div class="p-small-featured">
								<PublishIndexEntry v-for="post in items.slice(1)" :item="post" mode="small" :key="post.id" />

								<div v-if="item.url_without_domain" class="pw-btns">
									<NuxtLink :to="item.url_without_domain" class="hp-blog-all"><BaseCmsLabel code="all_posts" /></NuxtLink>
								</div>
							</div>
						</div>
					</div>
				</div>
			</BasePublishPostsWidget>
		</BasePublishCategory>		  		
	</BaseCmsPage>
</template>

<script setup>
	import { NuxtLink } from '#components';
	const {getAppUrl} = useApiRoutes();
	const {gtmTrack} = useGtm();
	const config = useAppConfig();
	const labels = useLabels();
	const mainSliderData = ref([]);
	const sliderOptions = {
		slidesPerView: 1,
		slidesPerGroup: 1,
		speed: 700,
		effect: 'fade',
		fadeEffect: {
			crossFade: true
		},
		loop: true,
		autoplay: {
			delay: 4500,
			disableOnInteraction: true
		},
		pagination: {
			enabled: true,
			clickable: true,
		},
		navigation: {enabled: true},
		on: {slideChange: (swiper) => {
				gtmTrack('promoSliderChange', {
					ecommerce: {
						promoView: {
							promotions: [{id: mainSliderData.value[swiper.activeIndex].id, name: mainSliderData.value[swiper.activeIndex].title, creative: 'banner' + (swiper.activeIndex + 1), position: 'slot' + (swiper.activeIndex + 1)}]
						}
					}
				}, {custom: true});
			}
		}
	}

	function onLoad(data) {
		mainSliderData.value = data.items;
	}


	onMounted(() => {
		gtmTrack('promoView', {
			ecommerce: {
				promoView:{
					promotions: [{id: 'promo_savjeti', name: 'Savjeti', creative: 'banner1', position: 'slot1'}],
				}
			}
		}, {custom: true});
		gtmTrack('promoView', {
			ecommerce: {
				promoView:{
					promotions: [{id: 'promo_proizvodi', name: 'Proizvodi', creative: 'banner2', position: 'slot2'}],
				}
			}
		}, {custom: true});
		gtmTrack('promoView', {
			ecommerce: {
				promoView:{
					promotions: [{id: 'promo_popusti', name: 'Popusti i događanja', creative: 'banner3', position: 'slot3'}],
				}
			}
		}, {custom: true});
	});

	

	function sendEvents(items){
		if (items.length) {
			items.forEach(item => {
				gtmTrack('view_promotion', {
					ecommerce: {
						items: [],
						promotion_id: item.code,
						promotion_name: item.title,
					}
				}, {custom: true});
			});
		}
	}
	function onLoadedProducts(products){
		if(products?.items.length){
			sendProductImpressions(products.items, "special");
		}
	}
</script>

<style lang="less" scoped>
	.main-slider{
		max-width: 1920px; margin:auto; min-height:713px; position: relative;
		.swiper-initialized{
			.hp-inner-content-cnt{visibility: visible; opacity: 1;}
			.hp-slide{display: block !important;}
		}
		:deep(img){display: block; width: auto; height: auto; max-width: 100%; max-width: 100%;}
		:deep(.swiper-button){
			width: 50px; height: 50px; top: 50%; margin-top: -25px; position: absolute; font-size: 0; z-index: 1; cursor: pointer;
			&:before{font: 47px/47px var(--fonti); .icon-arrow-light(); color: var(--textColor); display: block;}
			&:hover{
				&:before{color: var(--primaryColor);}
			}
		}
		:deep(.swiper-button-next){
			right: 85px;
			&:before{.rotate(180deg);}
		}
		:deep(.swiper-button-prev){left: 85px;}		
		:deep(.swiper-pagination){
			position: absolute; bottom: 35px; left: 20px; right: 20px; z-index: 1; display: flex; justify-content: center;
			span{
				display: inline-block; margin: 0 6px; border-radius: unset; height: 18px; width: 18px; min-width: 18px; background: var(--primaryColor); border: 4px solid var(--primaryColor); cursor: pointer; 
				&.swiper-pagination-bullet-active{background: white;}
			}
		}		
	}
	.main-slider-single{
		.slick-dots{display: none;}
	}
	.hp-content-container{height:713px; position: absolute; top: 0; right: 0; bottom: 0; left: 0; display: flex; flex-flow: column; padding-top: 130px; padding-bottom: 130px; color: var(--white); max-width: var(--pageWiderWrapper); margin: 0 auto; justify-content: center;}
	.hp-inner-content-cnt{width: 760px; visibility: hidden; opacity: 0;}
	.hp-slide{
		display: block; width: 100%; height: auto;
		&:not(:first-child){display: none;}
	}
	.hp-badge-image{
		position: absolute; bottom: calc(~"100% - 45px"); right: -50px; max-width: 170px;
		img{display: block; width: auto; height: auto; max-width: 100%;}
	}
	.hp-slider-title{
		padding-top: 0; font-weight: bold; padding-bottom: 10px; padding-bottom: 5px;  font-size: 51px; line-height: 1.1; font-weight: bold; color: var(--primaryColor);
		span{font-weight: bold; }
	}
	.hp-slide-content{
		font-size: 33px; line-height: 1.2; color: var(--primaryColor);
		span,strong{font-weight: bold; color: var(--primaryColor);}
	}
	.hp-slide-link{font-size: 20px; margin: 15px 0 0; padding: 0 35px; min-width: unset; width: auto; box-shadow: 0 15px 20px 0 rgba(85,41,122,0.2);}	

	//promos
	.hp-promos{padding-bottom: 80px; content-visibility: auto; contain-intrinsic-size: 463px;
		.wider-wrapper{display: flex;}
	}
	.hp-promo{width: calc(~"100%/4 - 15px"); margin-right: 20px; .transition(box-shadow);
		&:nth-child(4n){margin-right: 0;}
	}
	.hp-promo-img{
		.transition(box-shadow);
		:deep(img){display: block; width: auto; height: auto; max-width: 100%; max-height: 100%;}
	}
	.hp-promo-cnt{padding: 25px 20px 0;}
	.hp-promo-headline{font-weight: 500; text-align: center; font-size: 16px; line-height: 21px; color: var(--primaryColor); padding-bottom: 5px;}
	.hp-promo-title{font-size: 20px; line-height: 26px; font-weight: normal; text-align: center;}
	a.hp-promo{text-decoration: none; color: var(--textColor);
		@media(min-width: @h){
			&:hover{
				.hp-promo-title{color: var(--primaryColor);}
				.hp-promo-img{box-shadow: 0 30px 40px 0 rgba(85,41,122,0.2);}
			}
		}
	}	

	.hp-blog{
		padding: 105px 0 100px; background: url(assets/images/bg-main.jpg) top left no-repeat; background-size: cover; content-visibility: auto; contain-intrinsic-size: 943px;
		.p-featured{padding-top: 0;}
		.publish-flex{margin: 0;}
	}
	.special-title{
		font-size: 42px; line-height: 52px; margin-left: 60px; padding-left: 68px; position: relative; font-weight: 300; padding-bottom: 25px; color: var(--textColor);
		&:before{.icon-contract; color: var(--primaryColor); position: absolute; display: block; left: 0; top: 1px; font: 46px/46px var(--fonti); }
	}
	.pw-btns{padding: 25px 0 0 270px;}
	.hp-blog-all{font-size: 16px; line-height: 20px; color: var(--primaryColor); text-decoration: none; box-shadow: 0 1px 0 var(--primaryColor); font-weight: normal;}
		
	.hp-special-products{padding: 0 0 15px; content-visibility: auto; contain-intrinsic-size: 677px;}
	.hp-special-products-title{
		padding-bottom: 45px; text-align: center; padding-left: 0; margin-left: 0;
		&:before{display: none;}
	}	
	

	
	
	
	
	@media(max-width: 1680px) {
		.main-slider{min-height: 575px;}
		.hp-content-container{height:auto;}
	}
	@media(max-width: @l) {
		.main-slider{min-height:490px;}
		.hp-content-container{padding-top: 90px; padding-bottom: 90px; max-width: 1250px;}
		.hp-slider-title{font-size: 48px; line-height: 58px;}
		
		.hp-promo-headline{font-size: 15px; line-height: 20px;}
		.hp-promo-cnt{padding: 20px 15px 0;}
		.hp-promo-title{font-size: 18px; line-height: 24px;}
		.hp-promo{margin-right: 15px; width: calc(~"100%/4 - 10px");}
		.hp-promos{padding-bottom: 70px;}	

		.hp-blog{padding: 85px 0 90px;}	

		.hp-special-products{padding: 0 0 15px;}
		.hp-special-products-title{padding-bottom: 40px;}		
	}
	@media(max-width: @t) {
		.main-slider{min-height:auto;}
		.hp-content-container{padding-top: 35px; padding-bottom: 35px; max-width: unset; margin: 0 30px;}
		.hp-inner-content-cnt{width: 470px;}
		.hp-slider-title{font-size: 34px; line-height: 42px;}
		.hp-slide-link{margin: 10px 0 0;}

		.hp-promos{padding-bottom: 40px;}
		.hp-promo{margin-right: 10px; width: calc(~"100%/4 - 8px");}
		.hp-promo-cnt{padding: 15px 10px 0;}
		.hp-promo-headline{font-size: 14px; line-height: 18px; padding-bottom: 4px;}
		.hp-promo-title{font-size: 16px; line-height: 22px;}	

		.hp-blog{padding: 50px 0;}	
		.pw-btns{padding: 15px 0 0 165px;
			.hp-blog-all{font-size: 14px; line-height: 18px;}
		}
	
		.special-title{font-size: 32px; line-height: 42px; margin-left: 30px; padding-left: 50px; padding-bottom: 18px;
			&:before{font: 35px/35px var(--fonti); top: 1px;}
		}		
	}
	@media(max-width: @tp) {
		.main-slider{
			:deep(.swiper-pagination){
				bottom: 20px; left: 15px; right: 15px; padding: 0;
				div{margin: 0 4px;width: 14px; min-width: 14px; height: 14px;}
			}
			:deep(.swiper-navigation){display: none;}
		}
		.hp-content-container{padding-top: 45px; padding-bottom: 20px; margin: 0 15px;}
		.hp-slider-title{font-size: 28px; line-height: 32px; max-width: unset;}

		.hp-promo-headline{font-size: 13px; line-height: 17px;}
		.hp-promo-title{font-size: 14px; line-height: 18px;}
		.hp-promos{padding-bottom: 30px;}
		.hp-promo{width: calc(~"100%/4 - 7px");}	

		.hp-blog{padding: 40px 0;}
		.hp-blog-title{padding-left: 43px;
			&:before{font: 30px/30px var(--fonti);}
		}	
		.pw-btns{padding: 12px 0 0 132px;}

		.special-title{font-size: 26px; line-height: 36px; padding-left: 50px; padding-bottom: 15px; margin-left: 20px;
			&:before{font: 32px/32px var(--fonti); top: 0;}
		}

		.hp-special-products{
			border: 0; padding: 0 0 25px;
			.wider-wrapper{margin: 0 25px 0 29px;}
		}
		.hp-special-products-title{margin-left: 0; padding-left: 0;}
	}
	@media(max-width: @m) {
		.main-slider{
			min-height:0;
			:deep(img){min-height:300px;}
			.swiper-initialized {	
				.hp-slide{text-decoration:none;}
				.hp-inner-content-cnt{max-height:83px; overflow:hidden;}
			}
			:deep(.swiper-pagination){
				bottom: 16px;
				span{
					width: 14px; height: 14px; min-width: 14px; margin: 0 4px; border-color: #fff; background: #fff;
					&.swiper-pagination-bullet-active{background: var(--primaryColor);}
				}
			}
		}	
		.hp-slide{
			&:before{ .pseudo(auto,124px); bottom: 0; left: 0; right: 0; background-image: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.75) 100%);}
		}
		.hp-content-container{justify-content: unset; text-align: center; padding: 0 15px; min-height:119px; top: auto; bottom: 46px; min-height: 0; margin: 0; text-shadow: 0px 2px 5px rgba(0, 0, 0, 0.5);}
		.hp-inner-content-cnt{width: auto;}		
		.hp-slider-title{font-size: 26px; line-height: 33px; padding-bottom: 0px; height:33px; overflow: hidden; color: #fff;}	
		.hp-slide-content{max-width: unset; color:#fff; height:20px; overflow:hidden; font-size: 14px; line-height:22px;}
		.hp-slide-link{margin-bottom: 0; width: 200px !important; min-width: 200px !important; display:none!important;}	


		.hp-promos .wider-wrapper{display: flex; width: 100%; overflow-x: scroll; overflow-y: hidden; -webkit-overflow-scrolling: touch; padding: 0; height: auto; flex-wrap: unset; visibility: visible; opacity: 1; margin: 0;
			&::-webkit-scrollbar{display: none;}
			&::-webkit-scrollbar-thumb{display: none;}
		}
		.hp-promo{
			width: 280px; margin-bottom: 10px; flex-shrink: 0; margin-right: 8px;
			&:first-child{margin-left: 15px;}
			&:last-child{margin-right: 15px;}
		}	
		
		.hp-promo-cnt{padding: 12px 16px 0;}
		.hp-promo-headline{font-size: 14px; font-weight: bold;}
		.hp-promo-title{font-size: 16px; line-height: 21px;}

		.hp-blog{padding: 40px 0 30px;}
		.pw-btns{padding: 0; text-align: center; padding-top: 10px;}	

		.special-title{
			font-size: 24px; line-height: 1.2; padding-left: 40px; margin-left: 0; font-weight: bold; padding-bottom: 12px;
			&:before{font-size: 26px; line-height: 26px;}
		}	

		.hp-special-products .wider-wrapper{margin: 0;}
		.hp-special-products-title{margin-left: 0; padding-left: 15px; padding-right: 15px;}

		.pw-btns .hp-blog-all{font-size: 16px;}			
	}

</style>
